import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./scrollTabsLayout.scss";
import classNames from "classnames";
import Card from "components/card/Card";
import { Badge } from "components/badge/Badge";
import MessageDisplay from "components/messageDisplay/MessageDisplay";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";

export type TScrollTabsSize = "sm" | "md";

export type TSection = {
  id: string;
  label: string;
  content: React.ReactNode;
  keywords?: Array<string>;
  badgeCount?: number;
  showContent?: boolean;
};

interface IProps {
  sections: Array<TSection>;
  size?: TScrollTabsSize;
  fillAvailableHeight?: boolean;
  showSectionTitles?: boolean;
  syncActiveTabOnScroll?: boolean;
  wrapSectionsInCard?: boolean;
  contentHeader?: React.ReactNode;
  emptyState?: { title: string; content: string };
  onTabClick?: (sectionId: string, index: number) => void;
  suppressActiveTab?: boolean;
}

const CARD_PADDING = 48;

export default function ScrollTabsLayout(props: IProps) {
  const {
    sections,
    size = "sm",
    fillAvailableHeight = false,
    emptyState,
    showSectionTitles,
    syncActiveTabOnScroll = true,
    wrapSectionsInCard = true,
    contentHeader,
    onTabClick: onTabClickProp,
    suppressActiveTab = false,
  } = props;
  const [activeTab, setActiveTab] = useState<string>(sections[0]?.id ?? "");
  const tabsLayoutRef = useRef<HTMLDivElement>(null);
  const contentListRef = useRef<HTMLDivElement>(null);
  const contentHeaderRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef([]);
  const manualScroll = useRef(false);
  const scrollUnlockTimeout = useRef<number | null>(null);

  const windowSize = useWindowSize();

  const [contentHeight, setContentHeight] = useState<number>(0);

  useEffect(() => {
    if (sections.length === 0) {
      return;
    }

    setActiveTab(current => {
      if (current && sections.some(section => section.id === current)) {
        return current;
      }

      return sections[0].id;
    });
  }, [sections]);

  useLayoutEffect(() => {
    if (fillAvailableHeight) {
      return;
    }

    const handleResize = () => {
      if (tabsLayoutRef.current) {
        const rect = tabsLayoutRef.current.getBoundingClientRect();
        setContentHeight(window.innerHeight - rect.top - CARD_PADDING);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [fillAvailableHeight]);

  useLayoutEffect(() => {
    const headerElement = contentHeaderRef.current;
    const contentListElement = contentListRef.current;

    if (!contentListElement) {
      return;
    }

    if (!contentHeader || !headerElement) {
      contentListElement.style.removeProperty("--scroll-tabs-section-scroll-offset");
      return;
    }

    const updateSectionScrollOffset = () => {
      const styles = window.getComputedStyle(headerElement);
      const marginBottom = Number.parseFloat(styles.marginBottom) || 0;
      const offset = headerElement.offsetHeight + marginBottom;

      if (offset > 0) {
        contentListElement.style.setProperty("--scroll-tabs-section-scroll-offset", `${offset}px`);
      } else {
        contentListElement.style.removeProperty("--scroll-tabs-section-scroll-offset");
      }
    };

    updateSectionScrollOffset();

    const resizeObserver = new ResizeObserver(updateSectionScrollOffset);
    resizeObserver.observe(headerElement);

    return () => resizeObserver.disconnect();
  }, [contentHeader]);

  useEffect(() => {
    if (!syncActiveTabOnScroll) {
      return;
    }

    const scrollEl = contentListRef.current;
    if (!scrollEl || sections.length === 0) {
      return;
    }

    const activationOffset = 120;

    const updateActiveTab = () => {
      if (manualScroll.current) {
        return;
      }

      const scrollTop = scrollEl.scrollTop;
      const atBottom = scrollEl.scrollHeight - scrollTop - scrollEl.clientHeight < 2;

      if (atBottom) {
        setActiveTab(sections[sections.length - 1].id);
        return;
      }

      let activeId = sections[0].id;
      const containerRect = scrollEl.getBoundingClientRect();

      sections.forEach((section, index) => {
        const el = elementRef.current[index];
        if (!el) {
          return;
        }

        const titleEl = showSectionTitles ? el.querySelector(".ui-scroll-tabs-layout-section-title") : null;
        const measureEl = (titleEl as HTMLElement | null) ?? el;
        const measureRelativeTop = measureEl.getBoundingClientRect().top - containerRect.top;

        if (measureRelativeTop <= activationOffset) {
          activeId = section.id;
        }
      });

      setActiveTab(activeId);
    };

    scrollEl.addEventListener("scroll", updateActiveTab, { passive: true });
    updateActiveTab();

    return () => scrollEl.removeEventListener("scroll", updateActiveTab);
  }, [sections, syncActiveTabOnScroll, showSectionTitles]);

  function scrollToSection(index: number) {
    const ref = elementRef.current[index];
    ref?.scrollIntoView({ block: "start", behavior: "smooth" });
  }

  function onTabClick(id: string, index: number) {
    const ref = elementRef.current[index];
    if (!ref) {
      return;
    }
    setActiveTab(id);

    if (onTabClickProp) {
      onTabClickProp(id, index);
      return;
    }

    scrollToSection(index);

    if (!syncActiveTabOnScroll) {
      return;
    }

    manualScroll.current = true;

    if (scrollUnlockTimeout.current !== null) {
      clearTimeout(scrollUnlockTimeout.current);
    }

    scrollUnlockTimeout.current = window.setTimeout(() => {
      manualScroll.current = false;
      scrollUnlockTimeout.current = null;
    }, 1000);
  }

  const hasSections = sections && sections.length > 0;
  const hasVisibleContent = sections.some(section => section.showContent !== false);
  const showSidebar = windowSize?.width > MOBILE_WIDTH && (hasSections || emptyState != null);
  const scrollAreaStyle =
    !fillAvailableHeight && contentHeight > 0
      ? { maxHeight: `${contentHeight}px`, overflowY: "auto" as const }
      : undefined;

  return (
    <div
      className={classNames(
        "ui-scroll-tabs-layout",
        `ui-scroll-tabs-layout--size-${size}`,
        fillAvailableHeight && "ui-scroll-tabs-layout--fill",
      )}
      ref={tabsLayoutRef}
    >
      {showSidebar ? (
        <div className="ui-scroll-tabs-layout-tabs-list">
          {hasSections
            ? sections.map((section, index) => {
                const isActive = !suppressActiveTab && section.id === activeTab;
                const showBadge = section.badgeCount != null && section.badgeCount > 0;

                return (
                  <button
                    key={section.id}
                    type="button"
                    className={classNames("ui-scroll-tabs-layout-tab", {
                      "ui-scroll-tabs-layout-tab-active": isActive,
                    })}
                    onClick={() => onTabClick(section.id, index)}
                  >
                    <span className="ui-scroll-tabs-layout-tab-text">
                      <span className="ui-scroll-tabs-layout-tab-label">{section.label}</span>
                      {showBadge ? (
                        <Badge type="grayblue" size="small" outline>
                          {section.badgeCount}
                        </Badge>
                      ) : null}
                    </span>
                  </button>
                );
              })
            : null}
        </div>
      ) : null}

      <div ref={contentListRef} className="ui-scroll-tabs-layout-content-list" style={scrollAreaStyle}>
        {contentHeader ? (
          <div ref={contentHeaderRef} className="ui-scroll-tabs-layout-content-header">
            {contentHeader}
          </div>
        ) : null}

        {hasSections && hasVisibleContent ? (
          sections.map((section, index) => {
            if (section.showContent === false) {
              return null;
            }

            return (
              <section
                key={section.id}
                id={section.id}
                ref={ref => {
                  elementRef.current[index] = ref;
                }}
              >
                {wrapSectionsInCard ? (
                  <Card>
                    <Card.Section>
                      {showSectionTitles ? (
                        <h1 className="ui-scroll-tabs-layout-section-title">{section.label}</h1>
                      ) : null}
                      {section.content}
                    </Card.Section>
                  </Card>
                ) : (
                  section.content
                )}
              </section>
            );
          })
        ) : emptyState ? (
          <MessageDisplay title={emptyState.title} icon="magnifying-glass" content={emptyState.content} />
        ) : null}
      </div>
    </div>
  );
}
